import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

import CalendarMonth from "@mui/icons-material/CalendarMonth";
import {
  Box,
  ClickAwayListener,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { ApiAllRoutesItem } from "@/api/routes";
import { AIRPORT_MAP } from "@/constants/airport";
import { NEED_BACKGROUND_PROGRAM, airlineBgColor } from "@/constants/config";
import { PROGRAM_IMG } from "@/constants/programs";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { getBankImgByProgram } from "@/utils";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    // s: "seconds",
    // m: "1 minute",
    // mm: "%d minutes",
    s: "just now",
    m: "minutes ago",
    mm: "minutes ago",
    h: "1 hour ago",
    hh: "%d hours ago",
    d: "1 day ago",
    dd: "%d days ago",
    M: "1 month ago",
    MM: "%d months ago",
    y: "1 year ago",
    yy: "%d years ago",
  },
});

const RoutesItem = (props: {
  data: ApiAllRoutesItem;
  onLoad: () => void;
  showToolTip: boolean;
  onCloseToolTip: () => void;
}) => {
  const { data } = props;
  const airports = data.route.split("-");

  const theme = useThemeData();

  const isBreakPoint = useMediaQuery("(min-width:1200px)");
  const { isMobile } = usePlatform();
  const bankImgs = getBankImgByProgram(data.program);
  return (
    <Grid
      container
      className={`px-[20px] py-[15px]`}
      sx={{ minHeight: 100 }}
      onClick={() => {
        window.open(`/route-detail?route=${data.route}&program=${data.program}`, "_blank");
      }}
    >
      <Grid item xs={2} md={2}>
        <Box
          className={
            isMobile
              ? "rounded-[3px] p-[4px] inline-flex items-center h-[100%]"
              : "rounded-[3px] p-[4px] inline-flex items-center h-[100%] ml-[25px]"
          }
          sx={{
            backgroundColor:
              theme.isDarkMode && NEED_BACKGROUND_PROGRAM.includes(data.program)
                ? airlineBgColor
                : "transparent",
          }}
        >
          <img
            className="w-[50px] min-[700px]:w-[90px]"
            onLoad={props.onLoad}
            src={PROGRAM_IMG[data.program]}
            alt=""
          />
        </Box>
      </Grid>
      <Grid item xs={10} md={5}>
        <div className="flex flex-col items-center h-[100%] justify-center ml-[20px]">
          <div className="text-left min-[900px]:text-center w-[100%]">
            <Typography className="inline" fontSize={14}>
              {AIRPORT_MAP[airports[0]]?.city}({airports[0]})
            </Typography>
            <Typography className="inline" fontSize={14}>
              {" - "}
            </Typography>
            <Typography className="inline" fontSize={14}>
              {AIRPORT_MAP[airports[1]]?.city}({airports[1]})
            </Typography>
          </div>

          <Typography
            variant="caption"
            fontSize={13}
            className="w-[100%] text-left min-[900px]:text-center"
          >
            Seen: {data.update_time ? dayjs(data.update_time * 1000).fromNow() : "few days ago"}
          </Typography>
        </div>
      </Grid>
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          marginTop: isBreakPoint ? 0 : "10px",
        }}
      >
        <div className="flex flex-row items-center justify-between min-[900px]:justify-end h-[100%]">
          {bankImgs.length > 0 ? (
            <Box
              className="flex flex-wrap rounded-[6px] pl-[6px] pb-[10px] justify-center mr-[20px]"
              sx={{
                writingMode: "vertical-lr",
                maxHeight: "70px",
                backgroundColor: theme.isDarkMode ? "rgba(255, 255, 255, 0.16)" : "#FAFAFA",
              }}
            >
              {bankImgs.map((url, index) => (
                <img
                  onLoad={props.onLoad}
                  src={url}
                  key={index}
                  alt=""
                  className="w-65px h-[20px] block mr-[10px] mt-[10px]"
                  style={{
                    writingMode: "horizontal-tb",
                  }}
                />
              ))}
            </Box>
          ) : (
            <div />
          )}

          <ClickAwayListener onClickAway={props.onCloseToolTip}>
            <Tooltip
              placement="top"
              title="Check the availability of the entire year!"
              arrow
              onClose={props.onCloseToolTip}
              open={props.showToolTip}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <IconButton
                size="small"
                onClick={() => {
                  window.open(
                    `/route-detail?route=${data.route}&program=${data.program}`,
                    "_blank"
                  );
                }}
              >
                <CalendarMonth color="primary" />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
        </div>
      </Grid>
    </Grid>
  );
};

export default RoutesItem;
